import React, { useMemo } from 'react';

import { LocalizeCurrency } from '@change-corgi/core/react/i18n';
import { buttonResetStyles } from '@change-corgi/design-system-legacy/reset';
import { Text } from '@change-corgi/design-system-legacy/typography';

type Props = {
	amount: number;
	currency: string;
	isSelected: boolean;
	onClick: () => void;
	dataQaAttribute?: string;
	isSunriseEnabled: boolean;
};

export function AmountButton({
	amount,
	isSelected,
	onClick,
	currency,
	dataQaAttribute,
	isSunriseEnabled,
}: Props): React.JSX.Element {
	const colorStyles = useMemo(() => {
		if (isSelected)
			return {
				color: 'typography-primary',
				backgroundColor: 'rgba(21, 125, 185, 0.08)',
				borderColor: 'secondary-blue',
				...(isSunriseEnabled && {
					backgroundColor: 'sunrise.semantic.fill.accent.primary.weaker',
					borderColor: 'sunrise.semantic.fill.accent.primary.strong',
				}),
			};
		return {
			color: 'typography-primary',
			backgroundColor: 'primary-white',
			borderColor: 'neutral-grey100',
			...(isSunriseEnabled && {
				borderColor: 'sunrise.semantic.fill.weak',
			}),
		};
	}, [isSelected, isSunriseEnabled]);

	return (
		<button
			type="button"
			data-amount={amount}
			data-testid="payment-form-amount-button"
			data-qa={dataQaAttribute || 'contribution-amount-button'}
			onClick={onClick}
			aria-pressed={isSelected}
			aria-label={`${amount} ${currency}`}
			sx={{
				...buttonResetStyles,
				borderStyle: 'solid',
				borderWidth: isSelected ? '2px' : '1px',
				borderRadius: 'standard',
				width: '100%',
				height: '48px',
				fontWeight: 'bold',
				// eslint-disable-next-line @typescript-eslint/naming-convention
				':focus-within': {
					outlineWidth: '1px',
					outlineColor: 'secondary-blue',
					outlineStyle: 'solid',
				},
				...colorStyles,
			}}
		>
			<Text size="small" fontWeight="semibold">
				<LocalizeCurrency value={amount} currency={currency} />
			</Text>
		</button>
	);
}
